import React from 'react';
import { WatermarkSettings, WatermarkTextConfig } from '../services/api';

// Mock data for watermark preview
const MOCK_DATA = {
  owner_company_name: 'Acme Corporation',
  recipient_company_name: 'Tech Solutions Inc.',
  owner_email: 'john.doe@acme.com',
  recipient_email: 'jane.smith@techsolutions.com',
  recipient_ip_address: '192.168.1.100',
  timestamp: new Date().toLocaleString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit', 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit'
  }),
  file_name: 'confidential-report.pdf',
  file_id: '550e8400-e29b-41d4-a716-446655440000',
  device_name: 'MacBook Pro',
  device_id: 'device-abc123',
};

/**
 * Builds watermark text from text_config
 * If custom_text is enabled, returns only the custom_text_value
 * Otherwise, builds text from enabled config options
 */
export function buildWatermarkText(textConfig: WatermarkTextConfig): string {
  // If custom text is enabled, return only the custom text value
  if (textConfig.custom_text && textConfig.custom_text_value) {
    return textConfig.custom_text_value;
  }

  // Build text from enabled options
  const parts: string[] = [];

  if (textConfig.owner_company_name) {
    parts.push(MOCK_DATA.owner_company_name);
  }
  if (textConfig.recipient_company_name) {
    parts.push(MOCK_DATA.recipient_company_name);
  }
  if (textConfig.owner_email) {
    parts.push(MOCK_DATA.owner_email);
  }
  if (textConfig.recipient_email) {
    parts.push(MOCK_DATA.recipient_email);
  }
  if (textConfig.recipient_ip_address) {
    parts.push(MOCK_DATA.recipient_ip_address);
  }
  if (textConfig.timestamp) {
    parts.push(MOCK_DATA.timestamp);
  }
  if (textConfig.file_name) {
    parts.push(MOCK_DATA.file_name);
  }
  if (textConfig.device_name) {
    parts.push(MOCK_DATA.device_name);
  }

  return parts.join(' | ');
}

/**
 * Gets CSS styles for watermark preview based on settings
 * Watermarks should repeat across the page
 * Rotation: -90° (top-left), 0° (horizontal), 90° (bottom-right)
 * For correct visual: vertical should have top part looking left, diagonal should be bottom-left to top-right
 */
export function getWatermarkPreviewStyles(
  settings: WatermarkSettings,
  orientation: 'landscape' | 'portrait' = 'landscape'
): React.CSSProperties {
  // Angle is in degrees (-90 to 90)
  // Invert angle for correct visual direction:
  // - Vertical (90°): top part should look left, so we use -90°
  // - Diagonal (45°): should be bottom-left to top-right, so we use -45°
  // - Horizontal (0°): stays 0°
  const angle = typeof settings.angle === 'number' ? settings.angle : 0;
  const invertedAngle = -angle; // Invert for correct visual direction

  // Font size - make it bigger for preview
  const fontSize = (settings.font_size || 12) * 2; // Double the size for preview

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    fontFamily: settings.font || 'Arial',
    fontSize: `${fontSize}px`,
    color: settings.color || '#000000',
    opacity: settings.opacity || 0.5,
    lineHeight: `${fontSize * 1.2}px`, // Fixed line height based on font size
    transform: `rotate(${invertedAngle}deg)`,
    transformOrigin: 'center center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    pointerEvents: 'none',
    // Create repeating pattern
    backgroundImage: `repeating-linear-gradient(
      ${invertedAngle}deg,
      transparent,
      transparent ${fontSize * 2}px,
      currentColor ${fontSize * 2}px,
      currentColor ${fontSize * 2.5}px
    )`,
    backgroundSize: '200% 200%',
    backgroundPosition: '0 0',
  };
}

